import { useMemo, useState } from 'react';
import { CpmButton, CpmChip } from '../../../components/design-system';

interface InPlaceTagToggleProps {
    options: string[];
    selected: string[];
    onChange: (next: string[]) => void;
    allowCreate?: boolean;
    disabled?: boolean;
}

function tagLabel(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && 'tag' in value) {
        const tag = (value as { tag?: unknown }).tag;
        return typeof tag === 'string' ? tag : '';
    }
    return '';
}

function sameTag(a: unknown, b: unknown): boolean {
    const left = tagLabel(a);
    const right = tagLabel(b);
    return Boolean(left) && left.toLowerCase() === right.toLowerCase();
}

export default function InPlaceTagToggle({
    options,
    selected,
    onChange,
    allowCreate = false,
    disabled = false,
}: InPlaceTagToggleProps) {
    const [draft, setDraft] = useState('');
    const tags = useMemo(() => {
        const seen = new Map<string, string>();
        [...options, ...selected].forEach((tag) => {
            const label = tagLabel(tag);
            if (!label) return;
            const key = label.toLowerCase();
            if (!seen.has(key)) seen.set(key, label);
        });
        return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
    }, [options, selected]);

    const toggle = (tag: string) => {
        if (disabled) return;
        const on = selected.some((item) => sameTag(item, tag));
        onChange(on ? selected.filter((item) => !sameTag(item, tag)) : [...selected, tag]);
    };

    const addDraft = () => {
        const next = draft.trim();
        if (!next || disabled) return;
        if (!selected.some((item) => sameTag(item, next))) {
            onChange([...selected, next]);
        }
        setDraft('');
    };

    const selectedLabels = selected.map(tagLabel).filter(Boolean);
    const summary =
        selectedLabels.length === 0
            ? 'No tags on this template. Tags are optional — click one above to put it on.'
            : `${selectedLabels.length} ${selectedLabels.length === 1 ? 'tag' : 'tags'} on: ${selectedLabels.join(', ')}`;

    return (
        <div className="flex flex-col">
            {allowCreate ? (
                <div className="flex items-center" style={{ gap: 8 }}>
                    <input
                        value={draft}
                        disabled={disabled}
                        placeholder="Type a tag and press Enter"
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                addDraft();
                            }
                        }}
                        style={{
                            flex: 1,
                            minWidth: 0,
                            boxSizing: 'border-box',
                            height: 36,
                            padding: '0 12px',
                            borderRadius: 10,
                            border: '1.5px solid var(--cpm-border-strong)',
                            fontSize: 13,
                            fontWeight: 600,
                            color: 'var(--cpm-text-primary)',
                            outline: 'none',
                            background: '#fff',
                        }}
                    />
                    <CpmButton
                        variant="outline"
                        size="sm"
                        disabled={disabled || !draft.trim()}
                        onClick={addDraft}
                    >
                        Add
                    </CpmButton>
                </div>
            ) : null}
            {tags.length > 0 ? (
                <div
                    className="flex items-center flex-wrap"
                    style={{ gap: 6, marginTop: allowCreate ? 10 : 0 }}
                >
                    {tags.map((tag) => (
                        <CpmChip
                            key={tag}
                            active={selected.some((item) => sameTag(item, tag))}
                            disabled={disabled}
                            onClick={() => toggle(tag)}
                        >
                            {tag}
                        </CpmChip>
                    ))}
                </div>
            ) : null}
            {allowCreate ? (
                <div
                    style={{
                        marginTop: 8,
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: 'var(--cpm-text-secondary)',
                    }}
                >
                    {summary}
                </div>
            ) : null}
        </div>
    );
}
